'use client';

import { memo, useState, useRef, useCallback } from 'react';
import { analyticsEvents, setEnhancedConversionData } from '@/components/analytics/GoogleAnalytics';
import { collectAttribution } from '@/lib/attribution';
import { TurnstileWidget } from '@/components/ui/TurnstileWidget';

import type { ContactFormProps } from '@routeroyal/theme-core';

const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

interface AllysContactFormProps extends ContactFormProps {
  messages?: Record<string, unknown>;
}

export const AllysContactForm = memo(function AllysContactForm({ 
  recipientEmail,
  fields = ['firstName', 'lastName', 'email', 'message'],
  onSubmit,
  messages,
}: AllysContactFormProps) {
  const formMsg = (messages?.contact as Record<string, unknown>)?.form as Record<string, unknown> | undefined;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const formStartedRef = useRef(false);

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const handleFormFocus = () => {
    if (!formStartedRef.current) {
      formStartedRef.current = true;
      analyticsEvents.formStart('contact_form');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      if (onSubmit) {
        await onSubmit(formData);
      } else {
        const attribution = collectAttribution();
        await fetch(`/api/contact`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            recipientEmail,
            metadata: attribution,
            turnstileToken: turnstileToken || undefined,
          }),
        });
      }
      setSubmitStatus('success');

      const conversionData: Parameters<typeof setEnhancedConversionData>[0] = {
        email: formData.email,
      };
      if (formData.phone) conversionData.phone = formData.phone;
      if (formData.firstName) conversionData.firstName = formData.firstName;
      if (formData.lastName) conversionData.lastName = formData.lastName;
      setEnhancedConversionData(conversionData);

      analyticsEvents.formSubmit('contact_form', window.location.pathname);
      analyticsEvents.generateLead('contact_form');

      setFormData({ firstName: '', lastName: '', email: '', phone: '', message: '' });

      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'form_submission',
          form_name: 'contact_form',
          form_location: window.location.pathname,
        });
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-200">
        <h2 className="text-3xl font-light text-gray-900 mb-6 text-center">
          {safeGetString(formMsg?.title, 'Get in Touch')}
        </h2>

        <form onSubmit={handleSubmit} onFocusCapture={handleFormFocus} className="space-y-6">
          {fields.includes('firstName') && (
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                {safeGetString(formMsg?.firstName, 'First Name')}
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                placeholder={safeGetString(formMsg?.firstNamePlaceholder, 'Your first name')}
              />
            </div>
          )}

          {fields.includes('lastName') && (
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                {safeGetString(formMsg?.lastName, 'Last Name')}
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                placeholder={safeGetString(formMsg?.lastNamePlaceholder, 'Your last name')}
              />
            </div>
          )}

          {fields.includes('email') && (
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {safeGetString(formMsg?.email, 'Email')}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                placeholder={safeGetString(formMsg?.emailPlaceholder, 'your.email@example.com')}
              />
            </div>
          )}

          {fields.includes('phone') && (
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                {safeGetString(formMsg?.phone, 'Phone')}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
                placeholder={safeGetString(formMsg?.phonePlaceholder, '+1 (555) 123-4567')}
              />
            </div>
          )}

          {fields.includes('message') && (
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                {safeGetString(formMsg?.message, 'Message')}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-colors resize-none"
                placeholder={safeGetString(formMsg?.messagePlaceholder, 'Your message...')}
              />
            </div>
          )}

          <TurnstileWidget
            onVerify={handleTurnstileVerify}
            onExpire={handleTurnstileExpire}
            className="flex justify-center"
          />

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-4 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? safeGetString(formMsg?.sending, 'Sending...') : safeGetString(formMsg?.submit, 'Send Message')}
          </button>

          {submitStatus === 'success' && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">
              {safeGetString(formMsg?.success, 'Thank you! Your message has been sent successfully.')}
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="p-4 bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/30 rounded-lg text-[var(--color-accent)] text-sm">
              {safeGetString(formMsg?.error, 'Something went wrong. Please try again later.')}
            </div>
          )}
        </form>
      </div>
    </div>
  );
});

